import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useEffect, useMemo, useState } from "react";
import { NewsTab } from "./tabs/NewsTab";
import { BranchesTab } from "./tabs/BranchesTab";
import { BatchesTab } from "./tabs/BatchesTab";
import { AssociationsTab } from "./tabs/AssociationsTab";
import { ProjectsTab } from "./tabs/ProjectsTab";
import { UsersTab } from "./tabs/UsersTab";
import { usePortalUserContext } from "@/admin/PortalUserContext";
import {
  assignmentSummaryLabel,
  grantsSummaryLabels,
  portalIsSuperUser,
  scopedPortalAssignments,
  tabIdsForGrants,
} from "@/lib/portalSession";
import { ScopedAdminLanding } from "./ScopedAdminLanding";
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  FolderKanban,
  GraduationCap,
  Handshake,
  House,
  Newspaper,
  UserCog,
} from "lucide-react";

const ALL_TABS = [
  { id: "overview", label: "Home" },
  { id: "news", label: "News" },
  { id: "projects", label: "Projects" },
  { id: "branches", label: "Branch" },
  { id: "batches", label: "Batch" },
  { id: "associations", label: "Association" },
  { id: "users", label: "Users" },
] as const;

type TabId = (typeof ALL_TABS)[number]["id"];

const TAB_ICONS: Record<TabId, LucideIcon> = {
  overview: House,
  news: Newspaper,
  branches: Building2,
  batches: GraduationCap,
  associations: Handshake,
  projects: FolderKanban,
  users: UserCog,
};

export function AdminDashboard() {
  const portal = usePortalUserContext();
  const visibleIds = useMemo(() => {
    if (!portal) return [] as TabId[];
    const ids = tabIdsForGrants(portal.grants);
    return ids.filter((id): id is TabId => ALL_TABS.some((t) => t.id === id));
  }, [portal]);
  const tabs = useMemo(() => {
    const byId = new Map(ALL_TABS.map((t) => [t.id, t]));
    return visibleIds
      .map((id) => byId.get(id))
      .filter((t): t is (typeof ALL_TABS)[number] => t !== undefined);
  }, [visibleIds]);
  const [tab, setTab] = useState<TabId>(() => visibleIds[0] ?? "overview");

  useEffect(() => {
    if (visibleIds.length && !visibleIds.includes(tab)) {
      setTab(visibleIds[0]);
    }
  }, [visibleIds, tab]);

  if (!portal) return null;

  return (
    <div className="min-h-screen bg-brand-gray/40 text-brand-blue">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-brand-gray bg-white px-4 py-4">
        <div>
          <Link
            to="/"
            className="text-sm font-semibold text-brand-maroon hover:underline"
          >
            ← Back to website
          </Link>
          <h1 className="font-display text-2xl font-bold text-brand-blue">Admin</h1>
          <p className="mt-1 text-xs text-brand-blue/70">
            Signed in as {portal.email}
            <span className="font-semibold">
              {" "}
              (
              {grantsSummaryLabels(portal.grants).join(", ")}
              )
            </span>
          </p>
          {scopedPortalAssignments(portal.assignments).length > 1 ? (
            <div className="mt-2 max-w-md">
              <label htmlFor="portal-active-assignment" className="text-xs font-semibold text-brand-blue">
                Active assignment (News &amp; Projects)
              </label>
              <select
                id="portal-active-assignment"
                value={portal.activeAssignmentId ?? ""}
                onChange={(e) => portal.setActiveAssignmentId(e.target.value || null)}
                className="mt-1 block w-full max-w-xs rounded border border-brand-gray bg-white px-2 py-1.5 text-sm"
              >
                {scopedPortalAssignments(portal.assignments).map((a) => (
                  <option key={a.id} value={a.id}>
                    {assignmentSummaryLabel(a)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-brand-blue/60">
                You have more than one scoped role. Choose which branch, batch, or association News and
                Projects apply to.
              </p>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void supabase.auth.signOut()}
          className="rounded-full border border-brand-gray px-4 py-2 text-sm font-semibold hover:bg-brand-gray/60"
        >
          Sign out
        </button>
      </header>
      <div className="mx-auto flex max-w-7xl flex-col md:flex-row">
        <aside className="border-b border-brand-gray bg-white md:w-52 md:border-b-0 md:border-r md:shrink-0">
          <nav className="flex flex-row gap-1 overflow-x-auto p-2 md:flex-col md:overflow-visible">
            {tabs.map((t) => {
              const Icon = TAB_ICONS[t.id];
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm font-semibold md:w-full ${
                    tab === t.id
                      ? "bg-brand-blue text-white"
                      : "text-brand-blue hover:bg-brand-gray/60"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>
        <div className="flex-1 p-4 md:p-8">
          {tab === "overview" && (
            <ScopedAdminLanding
              sections={tabs.filter((t) => t.id !== "overview")}
              onSelectSection={(id) => setTab(id as TabId)}
            />
          )}
          {tab === "news" && <NewsTab />}
          {tab === "branches" && <BranchesTab />}
          {tab === "batches" && <BatchesTab />}
          {tab === "associations" && <AssociationsTab />}
          {tab === "projects" && <ProjectsTab />}
          {tab === "users" && portalIsSuperUser(portal.grants) && <UsersTab />}
        </div>
      </div>
    </div>
  );
}
